import { useEffect, useMemo, useState } from "react"

import useSources from "~/ui-rtk/hooks/sources"
import {
  Button,
  DateRangePicker,
  MultiSelect,
} from "~/ui-rtk/components/ui/controls"
import {
  AggregationType,
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"

import {
  type TBrandMediaOptimizationTableId,
  type TBrandMediaOptimizationTable,
} from "~/ui-rtk/pages/BrandMedia/Optimization/connect"

import { Breadcrumbs } from "./components"
import AnalyzeVisibleAdsButton, {
  selectedMetaAdIds,
  useVisibleRows,
} from "./components/AnalyzeVisibleAdsButton"
import type { RowSelectionState } from "@tanstack/react-table"
import { Chart, TableVisualization } from "~/ui-rtk/components/ui/charts"

import { BRAND_HEALTH_OVERVIEW_SOURCES_TO_BRAND_MEDIA_OPTIMIZATIONS_SOURCES_MAP } from "~/ui-rtk/constants/brand-media"

import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type {
  TableVisualizationColumn,
  TColumnFiltersState,
} from "~/ui-rtk/constants/table-visualization-types"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import useDrawer from "~/ui-rtk/hooks/drawer"
import { DRAWER_TYPE, TDrawerConfig } from "~/ui-rtk/constants/drawer"
import { TCubeFilterOptions } from "~/ui-rtk/shared/types/charts"
import {
  SOURCE_ID_TO_MEDIA_CHANNEL_MAPPING,
  TBrandMediaDashboardCharts,
  URL_PARAM_TO_WIDGET_MAP,
  VISUALIZATION_TABLE,
} from "./constants"
import {
  URL_ADSET_ID_PARAM_KEY,
  URL_CAMPAIGN_ID_PARAM_KEY,
  URL_WIDGET_PARAM_KEY,
} from "~/ui-rtk/constants/url-params"
import BrandMediaOptimizationSortedSection from "./components/SortedSection/SortedSection"
import { Link } from "react-router-dom"
import { usePeriod, DATE_FORMAT, isValidDate } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"

import { useLastUpdateDate, useCubeQuery } from "~/ui-rtk/hooks/cube"
import { type Query as TCubeQuery } from "@cubejs-client/core"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import useUrlSourceKeys from "~/ui-rtk/hooks/url-source-keys"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import useProductHandleOptions from "~/ui-rtk/hooks/product-handle-options"
import {
  convertToCubeFilters,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"
import MarathonDataLogo from "~/ui-rtk/components/ui/svg/Logo"
import useMediaOptimizationMediaChannels from "~/ui-rtk/hooks/media-optimization-media-channel"
import { capitalize } from "~/ui-rtk/utils/format"
import { Card, Loader, RichTooltip } from "~/ui-rtk/components/ui/common"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useMobileView from "~/ui-rtk/hooks/mobile-view"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import { SOURCES_MAP } from "~/ui-rtk/constants/sources"
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"
import BetaIcon from "~/ui-rtk/components/ui/common/BetaIcon"
import HighlightToggle from "~/ui-rtk/components/ui/controls/HighlightToggle/HighlightToggle"
import useRevenueFromSearch from "~/ui-rtk/hooks/revenue-from-search"
import {
  InitiativeTypeContainer,
  InitiativeTypeDropdown,
  useInitiativeTypeManagement,
  buildAdsetIdsByCampaign,
  getRowChildAdsetIds,
} from "~/ui-rtk/components/features/initiative-type"

export interface TBrandMediaDashboardProps {
  title: string
  slug: string
  periodPicker?: { visible: boolean }
  items: TBrandMediaDashboardCharts
  parent: {
    slug: string
    title: string
  }
}
const dayjs = useDate()

const maxDateRestriction = dayjs().toDate()

const BEST_WORST_ENABLED = false
const BRAND_MEDIA_ID = "brand_media"

function BrandMediaOptimizationContent({
  title,
  items,
  slug,
  parent,
}: TBrandMediaDashboardProps) {
  const { params, setParams } = useURLSearchParams()
  const { sources } = useSources()
  const { setPeriod, setComparePeriod } = usePeriod()
  const { openDrawer, closeDrawer } = useDrawer()
  const currentCompany = useCurrentCompany()
  const isMobile = useMobileView()
  // Bulk creative analysis acts on the rows the Ads table is
  // showing right now; the table reports them through `onRowsRendered`.
  const [visibleRows, onRowsRendered] = useVisibleRows()
  // Row ticks on the Ads table pick specific ads for that analysis (MD-5612).
  // TanStack keys the selection by row index, so it only means something for
  // the page it was made on: a new page/sort/filter clears it.
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  useEffect(() => {
    setRowSelection({})
  }, [visibleRows])
  const handleRowSelectionChange = (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState),
  ) => {
    setRowSelection(prev =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(prev)
        : updaterOrValue,
    )
  }
  const selectedAdIds = useMemo(
    () => selectedMetaAdIds(visibleRows, rowSelection),
    [visibleRows, rowSelection],
  )
  const [highlight, setHighlight] = useState(true)
  const isRevenueFromSearchEnabled = useRevenueFromSearch()
  const bestWorstEnabled = BEST_WORST_ENABLED || params.enable_best_worst

  const {
    getCurrentInitiativeType,
    getInitiativeTypeOptions,
    handleInitiativeTypeChange,
  } = useInitiativeTypeManagement()

  // Dedicated, UNFILTERED roster of every ad set per campaign (last 30 days).
  // The Ad Set tab table is filtered (by campaign/channel) and paginated, so its
  // rendered rows are an incomplete sibling set — applying an initiative-type
  // change "to the entire campaign" off those rows would silently miss ad sets.
  // This query has no campaign/channel filter so the cascade covers all siblings.
  const adsetSiblingsQuery: TCubeQuery = useMemo(() => {
    if (!currentCompany?.id) {
      return null as unknown as TCubeQuery
    }
    const queryPrefix = "company_media_metrics_adsets"
    const endDate = new Date()
    const startDate = new Date(endDate)
    startDate.setDate(startDate.getDate() - 30)

    const baseQuery: TCubeQuery = {
      measures: [`${queryPrefix}.spend`],
      dimensions: [`${queryPrefix}.campaign_id`, `${queryPrefix}.adset_id`],
      filters: [],
      timeDimensions: [{ dimension: `${queryPrefix}.md_date` }],
    }

    return polyfillCubeQueryWithTimeDimension(baseQuery, {
      dateRange: [startDate, endDate] as [Date, Date],
    })
  }, [currentCompany?.id])

  const { resultSet: adsetSiblingsResultSet } = useCubeQuery(adsetSiblingsQuery)

  // Map each campaign to its full set of ad set IDs so an Ad Set tab
  // initiative-type change can be applied to the whole campaign by cascading to
  // every sibling ad set (see InitiativeTypeContainer.performInitiativeTypeChange).
  const adsetIdsByCampaign = useMemo(
    () =>
      buildAdsetIdsByCampaign(
        (adsetSiblingsResultSet?.rawData() ?? []) as Array<
          Record<string, unknown>
        >,
      ),
    [adsetSiblingsResultSet],
  )

  // Get granularity from period hook like the social posts dashboard
  const { granularity } = usePeriod()

  const { amazonEnabled, toggleAmazon, ...depVarConfig } = useDepVar()

  const visibleWidgets: TBrandMediaDashboardCharts = useMemo(
    (): TBrandMediaDashboardCharts =>
      Object.keys(items)
        .filter(chartId => {
          const { requiredSources } =
            items[chartId as unknown as TBrandMediaOptimizationTableId]
          return (
            !requiredSources?.length ||
            requiredSources?.some((source: string) => sources?.has(source))
          )
        })
        .reduce(
          (
            filteredCharts: Record<
              TBrandMediaOptimizationTableId,
              TBrandMediaOptimizationTable
            >,
            chartId: string,
          ) => {
            filteredCharts[chartId as TBrandMediaOptimizationTableId] =
              items[chartId as unknown as TBrandMediaOptimizationTableId]
            return filteredCharts
          },
          {} as Record<
            TBrandMediaOptimizationTableId,
            TBrandMediaOptimizationTable
          >,
        ),
    [items, sources],
  )

  const defaultVisibleChart = useMemo(
    () =>
      visibleWidgets[
        Object.keys(
          visibleWidgets,
        )[0] as unknown as TBrandMediaOptimizationTableId
      ],
    [visibleWidgets],
  )
  const widgetURLSearchParam =
    params[URL_WIDGET_PARAM_KEY] ?? defaultVisibleChart?.id
  const campaignIdFromUrl = params[URL_CAMPAIGN_ID_PARAM_KEY]
  const adsetIdFromUrl = params[URL_ADSET_ID_PARAM_KEY]

  const activeWidget = useMemo(
    () =>
      widgetURLSearchParam && URL_PARAM_TO_WIDGET_MAP[widgetURLSearchParam]
        ? visibleWidgets[
            widgetURLSearchParam.toUpperCase() as TBrandMediaOptimizationTableId
          ]
        : defaultVisibleChart,
    [widgetURLSearchParam],
  )

  const { dateMetric, filterMetrics } = activeWidget?.lastDateChipProps ?? {}

  const { lastDate, isLoading: isLastUpdateDateLoading } = useLastUpdateDate({
    dateMetric,
    filterMetrics,
  })

  const { dateRange, compareRange } = useMinMaxDateRange(
    undefined,
    lastDate ? new Date(lastDate) : maxDateRestriction,
    AggregationType.DAILY,
    true,
    undefined,
    "last7Days",
  )

  const lastDateRestriction = useMemo(
    () => (lastDate ? new Date(lastDate) : maxDateRestriction),
    [lastDate, maxDateRestriction],
  )

  const chartProps = useMemo(
    () => VISUALIZATIONS_MAP[activeWidget?.widget]?.props,
    [activeWidget],
  )

  const queryPrefix = useMemo(() => chartProps?.queryPrefix, [chartProps])

  const defaultColumnKeys = useMemo(() => {
    const props = VISUALIZATIONS_MAP[activeWidget?.widget]?.props
    if (!isRevenueFromSearchEnabled) {
      const revenueFromSearchColumns = [
        "revenue_from_search",
        "revenue_from_search_roas",
      ]
      return props?.columnConfig
        .filter(
          (column: TableVisualizationColumn) =>
            !revenueFromSearchColumns.includes(column.key),
        )
        .map((column: TableVisualizationColumn) => column.key)
    }
    return props?.columnConfig.reduce(
      (
        acc: string[],
        column: TableVisualizationColumn & { disabled: boolean },
      ) => {
        if (!column.disabled) {
          acc.push(column.key)
        }
        return acc
      },
      [],
    )
  }, [activeWidget, isRevenueFromSearchEnabled])

  const querySortBy = params.sort_by
  const sortBy = useMemo(
    () =>
      querySortBy
        ? `${queryPrefix}.${querySortBy}`
        : `${queryPrefix}.${chartProps?.sortBy}`,
    [querySortBy, queryPrefix, chartProps],
  )
  const chartPropsSorting = useMemo(
    () =>
      sortBy
        ? [
            {
              id: sortBy,
              desc: true,
            },
          ]
        : [],
    [chartProps],
  )
  const { columnFilters, setColumnFilters } =
    useUrlColumnFilters(BRAND_MEDIA_ID)
  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    BRAND_MEDIA_ID,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(() => {
    const options = chartProps?.columnConfig.map(
      (column: TableVisualizationColumn) =>
        ({
          value: column.key,
          label: column.header,
          icon: column.isBrand ? <MarathonDataLogo /> : null,
        }) as TMultiSelectOption,
    )

    return options || []
  }, [chartProps])

  // Product filter options (searchable-select) for the Ads tab. The resolver
  // switches on the BARE additionalFilters key; non-product filters get [].
  const { productOptions, isLoading: isLoadingProductOptions } =
    useProductHandleOptions()
  const getSelectOptions = useMemo(
    () =>
      (columnKey: string): TMultiSelectOption[] => {
        switch (columnKey) {
          case "all_product_handles_str":
            return productOptions
          default:
            return []
        }
      },
    [productOptions],
  )

  const {
    request,
    isLoading: isMediaChannelsLoading,
    mediaChannels,
  } = useMediaOptimizationMediaChannels(queryPrefix)

  const querySelectedSource = params.selected_sources
  const initialSources = useMemo(() => {
    if (querySelectedSource) {
      const brandMediaSources = querySelectedSource
        .split(",")
        .map(
          (sourceId: string) =>
            BRAND_HEALTH_OVERVIEW_SOURCES_TO_BRAND_MEDIA_OPTIMIZATIONS_SOURCES_MAP[
              sourceId
            ],
        )
      return [...new Set(brandMediaSources)].filter(source =>
        activeWidget?.requiredSources?.includes(source),
      )
    }
    return mediaChannels || []
  }, [querySelectedSource, activeWidget])

  const { sourceKeys, setSourceKeys } = useUrlSourceKeys(
    BRAND_MEDIA_ID,
    initialSources,
  )

  // const transformedSourceKeys = useMemo(
  //   () => sourceKeys.map(str => str.toLocaleLowerCase()),
  //   [sourceKeys],
  // )

  const sourcesOptions = useMemo(() => {
    if (
      !activeWidget?.requiredSources ||
      !mediaChannels ||
      isMediaChannelsLoading
    ) {
      return []
    }

    const options = mediaChannels.map(channel => ({
      value: channel,
      label: channel.split("_").map(capitalize).join(" "),
      isBeta: Array.from(sources.values()).find(
        source => source.id === channel.toUpperCase(),
      )?.isBeta,
    }))

    return options
  }, [activeWidget, sources, mediaChannels])

  const cubeFilters = useMemo(() => {
    const filters: Record<string, string | string[] | undefined | null> = {
      campaign_id: [
        VISUALIZATION_TABLE.AD_SET,
        VISUALIZATION_TABLE.AD,
      ].includes(activeWidget?.widget)
        ? params[URL_CAMPAIGN_ID_PARAM_KEY]
        : undefined,
      adset_id:
        activeWidget?.widget === VISUALIZATION_TABLE.AD
          ? params[URL_ADSET_ID_PARAM_KEY]
          : undefined,
      dep_var: depVarConfig.depVar,
      dep_var_scaler: depVarConfig.depVarScaler,
    }

    const baseQuery = convertToCubeFilters(queryPrefix, filters)

    return {
      and: [
        ...baseQuery,
        {
          or: sourceKeys.map((sourceId: string) => ({
            member: `${queryPrefix}.media_channel`,
            operator: "equals",
            values: [SOURCE_ID_TO_MEDIA_CHANNEL_MAPPING[sourceId] ?? sourceId],
          })),
        },
      ],
    } as TCubeFilterOptions
  }, [
    sourceKeys,
    queryPrefix,
    dateRange,
    compareRange,
    params,
    activeWidget,
    depVarConfig,
  ])

  useEffect(() => {
    void request()
  }, [activeWidget, currentCompany])

  useEffect(() => {
    if (campaignIdFromUrl) {
      const hasCampaignFilter = columnFilters.find(
        filter =>
          filter.id === URL_CAMPAIGN_ID_PARAM_KEY &&
          filter.operator === "contains" &&
          filter.value === campaignIdFromUrl,
      )
      if (hasCampaignFilter) {
        setParams({
          [URL_CAMPAIGN_ID_PARAM_KEY]: undefined,
        })
        closeDrawer()
      } else {
        // Only add column filter if the column exists in the table configuration
        const campaignColumnExists = chartProps?.columnConfig?.some(
          (col: { key: string }) => col.key === URL_CAMPAIGN_ID_PARAM_KEY,
        )

        if (campaignColumnExists) {
          setColumnFilters((prevState: TColumnFiltersState | undefined) => {
            const newState = (
              prevState ? [...prevState] : []
            ) as TColumnFiltersState
            const filterIdx = newState.findIndex(
              filter =>
                filter.id === URL_CAMPAIGN_ID_PARAM_KEY &&
                filter.operator === "contains",
            )

            if (filterIdx >= 0) {
              newState[filterIdx].value = campaignIdFromUrl
            } else {
              newState.push({
                id: URL_CAMPAIGN_ID_PARAM_KEY,
                variant: "text",
                operator: "contains",
                value: campaignIdFromUrl,
              })
            }
            return newState
          })
        }
      }
    }
  }, [campaignIdFromUrl, columnFilters, setColumnFilters, chartProps])

  useEffect(() => {
    if (adsetIdFromUrl) {
      const hasAdsetFilter = columnFilters.find(
        filter =>
          filter.id === URL_ADSET_ID_PARAM_KEY &&
          filter.operator === "contains" &&
          filter.value === adsetIdFromUrl,
      )
      if (hasAdsetFilter) {
        setParams({
          [URL_ADSET_ID_PARAM_KEY]: undefined,
        })
        closeDrawer()
      } else {
        // Only add column filter if the column exists in the table configuration
        const adsetColumnExists = chartProps?.columnConfig?.some(
          (col: { key: string }) => col.key === URL_ADSET_ID_PARAM_KEY,
        )

        if (adsetColumnExists) {
          setColumnFilters((prevState: TColumnFiltersState | undefined) => {
            const newState = (
              prevState ? [...prevState] : []
            ) as TColumnFiltersState
            const filterIdx = newState.findIndex(
              filter =>
                filter.id === URL_ADSET_ID_PARAM_KEY &&
                filter.operator === "contains",
            )

            if (filterIdx >= 0) {
              newState[filterIdx].value = adsetIdFromUrl
            } else {
              newState.push({
                id: URL_ADSET_ID_PARAM_KEY,
                variant: "text",
                operator: "contains",
                value: adsetIdFromUrl,
              })
            }
            return newState
          })
        }
      }
    }
  }, [adsetIdFromUrl, columnFilters, setColumnFilters, chartProps])

  const changePeriod = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    void setPeriod(
      dayjs(start).format(DATE_FORMAT),
      dayjs(end).format(DATE_FORMAT),
    )

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const handleSetWidget = (widgetId: string) => {
    setParams({
      [URL_WIDGET_PARAM_KEY]: widgetId.toLowerCase(),
      [URL_CAMPAIGN_ID_PARAM_KEY]: undefined,
      [URL_ADSET_ID_PARAM_KEY]: undefined,
    })
    closeDrawer()
  }
  const handleOpenDrawer = (config: Partial<TDrawerConfig>) => {
    let drawerType: DRAWER_TYPE | null = null
    let component: string

    if (Object.values(VISUALIZATION_TABLE).includes(activeWidget?.widget)) {
      drawerType = DRAWER_TYPE.CampaingAdSetAdDetails
    }

    if (activeWidget?.widget === VISUALIZATION_TABLE.CAMPAIGN) {
      component = "CampaignDetails"
    } else if (activeWidget?.widget === VISUALIZATION_TABLE.AD_SET) {
      component = "AdSetDetails"
    } else if (activeWidget?.widget === VISUALIZATION_TABLE.AD) {
      component = "AdDetails"
    } else {
      component = ""
    }

    if (drawerType) {
      openDrawer({
        ...config,
        props: {
          ...config.props,
          component,
        },
        type: drawerType,
      } as TDrawerConfig)
    }
  }

  const columnConfigFiltered = useMemo(() => {
    const filtered = (chartProps?.columnConfig ?? []).filter(
      ({ key }: { key: string }) => (columnKeys as string[])?.includes(key),
    )

    // Enhance the adsets-table initiative_type column with an editable dropdown
    // that opens the shared confirmation modal and persists to Prisma.
    return filtered.map((col: TableVisualizationColumn) => {
      if (
        col.key === "initiative_type" &&
        (col as TableVisualizationColumn & { cellType?: string }).cellType ===
          "custom-component"
      ) {
        return {
          ...col,
          cellType: "custom-component",
          customComponent: ({
            rowData,
            onStopPropagation,
          }: {
            rowData: Record<string, unknown>
            cellValue: unknown
            onStopPropagation: (e: React.MouseEvent) => void
          }) => {
            const currentValue = getCurrentInitiativeType(rowData)
            const options = getInitiativeTypeOptions(rowData)
            const childAdsetIds = getRowChildAdsetIds(
              rowData,
              adsetIdsByCampaign,
            )
            return (
              <InitiativeTypeDropdown
                value={currentValue}
                options={options}
                onChange={value =>
                  handleInitiativeTypeChange(value, {
                    ...rowData,
                    childAdsetIds,
                  })
                }
                onStopPropagation={onStopPropagation}
              />
            )
          },
        }
      }
      return col
    })
  }, [
    chartProps?.columnConfig,
    columnKeys,
    getCurrentInitiativeType,
    getInitiativeTypeOptions,
    handleInitiativeTypeChange,
    adsetIdsByCampaign,
  ])

  // The Ads table gets a tick column to hand-pick ads for the
  // AI analysis. The filter bar keeps `columnConfigFiltered`: a tick column
  // is not something to filter or sort on.
  const isAnalyzableAdsTable = activeWidget?.widget === VISUALIZATION_TABLE.AD
  const tableColumnConfig = useMemo<TableVisualizationColumn[]>(
    () =>
      isAnalyzableAdsTable
        ? [
            {
              key: "select",
              header: "",
              type: "text" as const,
              width: 44,
              disableSorting: true,
              disableFiltering: true,
            },
            ...columnConfigFiltered,
          ]
        : columnConfigFiltered,
    [isAnalyzableAdsTable, columnConfigFiltered],
  )

  const renderTable = () => (
    <div className="mt-3">
      <Chart
        {...chartProps}
        highlightCells={chartProps?.highlightCells && highlight}
        chartId={activeWidget?.widget}
        urlParamsPrefix={BRAND_MEDIA_ID}
        className=""
        widgetType={VISUALIZATION_TYPE.TableVisualization}
        component={TableVisualization}
        dateRange={dateRange}
        compareRange={compareRange ?? undefined}
        comparisonEnabled={true}
        granularity={granularity}
        sources={sources}
        requiredSources={activeWidget?.requiredSources}
        hideAggSelector={true}
        cubeFilters={cubeFilters}
        queryPrefix={queryPrefix}
        useOrderQueryPrefix={true}
        defaultSorting={chartPropsSorting}
        defaultColumnKeys={defaultColumnKeys}
        summaryWidget={activeWidget?.summaryWidget}
        useLocalStoragePagination={!activeWidget?.summaryWidget}
        columnConfig={tableColumnConfig}
        enableCSVExport={true}
        csvExportFilename={activeWidget?.label || "optimization"}
        openDrawer={
          Object.values(VISUALIZATION_TABLE).includes(activeWidget?.widget)
            ? handleOpenDrawer
            : undefined
        }
        onRowsRendered={isAnalyzableAdsTable ? onRowsRendered : undefined}
        enableRowSelection={isAnalyzableAdsTable}
        rowSelection={isAnalyzableAdsTable ? rowSelection : undefined}
        onRowSelectionChange={
          isAnalyzableAdsTable
            ? (handleRowSelectionChange as (
                rowSelection: RowSelectionState,
              ) => void)
            : undefined
        }
      />
    </div>
  )

  return (
    <>
      <div
        className={cn({
          "flex items-center justify-between": !isMobile,
        })}
      >
        <Breadcrumbs slug={slug} title={title} parent={parent} />
      </div>
      {bestWorstEnabled && (
        <div className="mt-3">
          <BrandMediaOptimizationSortedSection
            visibleWidgets={Object.values(visibleWidgets)}
            dateRange={dateRange}
          />
        </div>
      )}

      <div className="my-system-lg">
        <TabsGroup
          options={Object.values(visibleWidgets).map(tab => ({
            label: tab.label,
            id: tab.id,
          }))}
          onChange={value =>
            handleSetWidget(Object.values(visibleWidgets)[value].id)
          }
          index={Object.values(visibleWidgets).findIndex(
            tab => tab.id === activeWidget?.id,
          )}
        >
          {tab => (
            <div className="flex items-center gap-system-xs">
              <span>{tab.label}</span>
            </div>
          )}
        </TabsGroup>
      </div>

      <div className="flex justify-between items-start gap-system-md mb-system-lg">
        <div className={cn({ "opacity-50": isMediaChannelsLoading })}>
          <div className="join">
            {sourcesOptions.map(({ value, label, isBeta }) => (
              <Button
                variant={{ variant: "solid", color: "blue" }}
                key={value}
                title={label}
                className={cn(
                  `join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm  rounded-system-md relative`,
                  { "bg-system-blue-800": !sourceKeys.includes(value) },
                  {
                    "bg-system-blue-600":
                      sourceKeys.length === 0 || sourceKeys.includes(value),
                  },
                )}
                onClick={() =>
                  setSourceKeys(
                    sourceKeys.includes(value)
                      ? sourceKeys.filter(sourceId => sourceId !== value)
                      : [...sourceKeys, value],
                  )
                }
              >
                {SOURCES_MAP[value?.toUpperCase() as keyof typeof SOURCES_MAP]
                  ?.icon && (
                  <img
                    src={
                      SOURCES_MAP[
                        value?.toUpperCase() as keyof typeof SOURCES_MAP
                      ].icon
                    }
                    alt={label}
                    className="w-4 h-4"
                  />
                )}
                <span
                  className={cn("font-[Lato] ", {
                    "text-base text-system-gray-300 font-normal":
                      !sourceKeys.includes(value),
                    "text-white font-semibold":
                      sourceKeys.length === 0 || sourceKeys.includes(value),
                  })}
                >
                  {label}
                </span>
                {isBeta && <BetaIcon />}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex justify-end items-stretch gap-system-sm">
          {isAnalyzableAdsTable && (
            <AnalyzeVisibleAdsButton
              rows={visibleRows}
              selectedAdIds={selectedAdIds}
            />
          )}
          {chartProps?.highlightCells && (
            <HighlightToggle
              isEnabled={highlight}
              toggle={() => setHighlight((val: boolean) => !val)}
            />
          )}
          {toggleAmazon && (
            <AmazonToggle
              amazonEnabled={amazonEnabled}
              toggleAmazon={toggleAmazon}
            />
          )}
          <MultiSelect
            label="Columns"
            options={columnOptions as TMultiSelectOption[]}
            value={columnOptions.filter((option: TMultiSelectOption) =>
              (columnKeys as string[]).includes(option.value),
            )}
            onChange={(options: TMultiSelectOption[]) =>
              setColumnKeys(options.map(({ value }) => value))
            }
          />
          <TableFiltersBar
            id={BRAND_MEDIA_ID}
            columnConfig={columnConfigFiltered}
            additionalFilters={chartProps?.additionalFilters}
            getSelectOptions={getSelectOptions}
            isLoadingOptions={isLoadingProductOptions}
          />
          {dateRange?.[0] &&
            dateRange?.[1] &&
            isValidDate(dateRange[0]) &&
            isValidDate(dateRange[1]) && (
              <DateRangePicker
                start={dateRange[0]}
                end={dateRange[1]}
                compareStart={
                  compareRange?.[0] && isValidDate(compareRange[0])
                    ? compareRange[0]
                    : undefined
                }
                compareEnd={
                  compareRange?.[1] && isValidDate(compareRange[1])
                    ? compareRange[1]
                    : undefined
                }
                comparisonEnabled={true}
                maxDate={lastDateRestriction}
                onChange={changePeriod}
              />
            )}
          <RichTooltip
            content={
              <LastUpdatedChip
                usedInTooltip={true}
                lastDate={lastDate}
                isLoading={isLastUpdateDateLoading}
              >
                Max date available in Page:
              </LastUpdatedChip>
            }
          >
            <Card className="p-system-xs relative h-system-2xl w-system-2xl flex items-center justify-center rounded-system-md bg-system-blue-700 border border-system-blue-600 cursor-pointer">
              <span className="absolute -top-system-3xs -right-system-3xs flex size-system-sm">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-system-brand-pink-400 opacity-75"></span>
                <span className="relative inline-flex size-system-sm rounded-full bg-system-brand-pink-500"></span>
              </span>
              <InformationCircleIcon className="w-system-lg h-system-lg" />
            </Card>
          </RichTooltip>
        </div>
      </div>
      {isMediaChannelsLoading ? (
        <div className="h-100 w-full flex items-center justify-center">
          <Loader size={50} />
        </div>
      ) : (
        <div className="mt-3">
          {activeWidget ? (
            renderTable()
          ) : (
            <div>
              It seems like you don't have active connection to show anything on
              this page. Please{" "}
              <Link
                className="link"
                to="/setup/connectors"
                title="Connector page"
              >
                review your connections.
              </Link>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default function BrandMediaOptimizationDashboard(
  props: TBrandMediaDashboardProps,
) {
  return (
    <InitiativeTypeContainer>
      <BrandMediaOptimizationContent {...props} />
    </InitiativeTypeContainer>
  )
}
